import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

/**
 * Opening splash: shows the full stacked brand logo (pod + wordmark) with a
 * masked line reveal, then sweeps up to reveal the hero. Locks scroll via
 * Lenis while active. Fires once per hard load.
 */
export const SplashScreen = ({ onDone }) => {
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const lenis = window.__lenis;
    lenis?.stop();
    // Prevent user scroll while splash is up (native fallback on touch devices).
    const prevBodyOverflow = document.body.style.overflow;
    const prevHtmlOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";

    const restore = () => {
      document.body.style.overflow = prevBodyOverflow;
      document.documentElement.style.overflow = prevHtmlOverflow;
      const l = window.__lenis;
      l?.start();
      l?.scrollTo(0, { immediate: true });
    };

    // Kick the exit animation
    const hold = setTimeout(() => setVisible(false), 2600);
    // Restore scroll a moment after the exit begins so the user can scroll
    // as soon as the splash is out of the way (safety net in case
    // onExitComplete never fires on some mobile browsers).
    const unlock = setTimeout(restore, 2600 + 1200);

    return () => {
      clearTimeout(hold);
      clearTimeout(unlock);
      restore();
    };
  }, []);

  const handleExitComplete = () => {
    document.body.style.overflow = "";
    document.documentElement.style.overflow = "";
    const lenis = window.__lenis;
    lenis?.start();
    lenis?.scrollTo(0, { immediate: true });
    onDone?.();
  };

  return (
    <AnimatePresence onExitComplete={handleExitComplete}>
      {visible && (
        <motion.div
          key="splash"
          initial={{ y: 0 }}
          exit={{ y: "-100%" }}
          transition={{ duration: 1.15, ease: [0.83, 0, 0.17, 1] }}
          className="fixed inset-0 z-[200] bg-cream flex items-center justify-center overflow-hidden"
          data-testid="splash-screen"
        >
          {/* Ambient gold radial */}
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(201,162,39,0.10),transparent_60%)]" />

          {/* Top / bottom rule marks */}
          <motion.div
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 1.2, delay: 0.2, ease: [0.65, 0, 0.35, 1] }}
            className="absolute top-8 left-8 right-8 h-[1px] bg-forest/25 origin-left"
          />
          <motion.div
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 1.2, delay: 0.35, ease: [0.65, 0, 0.35, 1] }}
            className="absolute bottom-8 left-8 right-8 h-[1px] bg-forest/25 origin-right"
          />

          {/* Meta chips */}
          <motion.span
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.7 }}
            className="absolute top-14 left-8 md:left-12 font-sans text-[11px] uppercase tracking-[0.32em] text-charcoal/55"
          >
            <span className="text-gold">§</span>&nbsp;&nbsp;Ongole · Prakasam · AP
          </motion.span>
          <motion.span
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.7 }}
            className="absolute top-14 right-8 md:right-12 font-serif italic text-charcoal/55 text-sm"
          >
            Elettaria&nbsp;cardamomum
          </motion.span>

          {/* Center lockup */}
          <div className="relative flex flex-col items-center px-6">
            {/* Pod — masked reveal */}
            <div className="relative overflow-hidden">
              <motion.img
                src="/logo.png"
                alt="The Cardamom Trading Co."
                initial={{ y: "108%", opacity: 0, scale: 1.02 }}
                animate={{ y: "0%", opacity: 1, scale: 1 }}
                transition={{
                  duration: 1.15,
                  delay: 0.25,
                  ease: [0.65, 0, 0.35, 1],
                }}
                className="block h-[54vh] max-h-[520px] min-h-[280px] w-auto object-contain will-change-transform"
                style={{ objectPosition: "center top" }}
                loading="eager"
                decoding="async"
              />
            </div>

            {/* Underline sweep */}
            <motion.div
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ delay: 1.15, duration: 0.9, ease: [0.65, 0, 0.35, 1] }}
              className="mt-4 h-[1px] w-40 md:w-56 bg-gold origin-left"
            />

            {/* Tagline */}
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.4, duration: 0.9 }}
              className="mt-5 font-serif italic text-charcoal/70 text-base md:text-lg tracking-wide"
            >
              Sourced with intent. Traded with care.
            </motion.p>
          </div>

          {/* Bottom counter */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.9, duration: 0.6 }}
            className="absolute bottom-14 left-0 right-0 flex items-center justify-between px-8 md:px-12 font-sans text-[11px] uppercase tracking-[0.32em] text-charcoal/50"
          >
            <span>Est. Ongole</span>
            <ProgressCounter />
            <span className="hidden md:inline">Volume · 01</span>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const ProgressCounter = () => {
  const [v, setV] = useState(0);
  useEffect(() => {
    let raf;
    const start = performance.now();
    const total = 2000;
    const tick = (now) => {
      const p = Math.min(1, (now - start) / total);
      setV(Math.round(p * 100));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);
  return (
    <span className="font-serif italic text-charcoal/60 text-lg tracking-normal">
      {String(v).padStart(3, "0")}
    </span>
  );
};

export default SplashScreen;
